import { Page } from '@progress/kendo-react-dropdowns';
import { Grid, GridColumn, GridCustomCellProps, GridHandle, GridNoRecords, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { Checkbox } from '@progress/kendo-react-inputs';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { WorkerClient } from '../../../../../client/worker-client';
import Coordinate from '../../../../../common/coordinate';
import Fuel from '../../../../../common/fuel';
import { RouteFlownItem } from '../../../../../flights/type';
import { DateTimeFormatter, UnitFormatter } from '../../../../../formatters';
import commonFormatter from '../../../../../formatters/common-formatter';
import useDebugFeatures from '../../../../../hooks/use-debug-features';
import { EventHistoryItem, FlightHistoryItemEnum } from '../../../../../messages/message-json';
import { EventDefinition } from '../../../../../monitoring/alerts/event-list/models';
import { MessageType, QueryFlightHistoryRequest } from '../../../../../services/actor-messages';
import { MapPaddingContext } from '../../../../contexts/map-padding-context';
import { useAppSelector } from '../../../../hooks/store-hooks';
import { useFlightWorker } from '../../../../hooks/use-flight-worker';
import useMap from '../../../../hooks/use-map';
import { useFocusedWorkspaceSelector } from '../../hooks/use-view';
type HistoryItem = (RouteFlownItem & { type: FlightHistoryItemEnum.position }) | EventHistoryItem;

const TAKE = 100;

function queryFlightHistory(worker: WorkerClient, request: QueryFlightHistoryRequest) {
    return worker.actor.sendAsync({
        type: MessageType.queryFlightHistory,
        data: request
    });
}

export function FlightHistoryTelerikWorks(props: { id: number }) {
    const eventDefinitions = useAppSelector((x) => x.global.monitorSettings.eventDefinitions);
    const flight = useFocusedWorkspaceSelector((x) => x.flights.selected.find((x) => x.details.id === props.id));
    const [items, setItems] = React.useState<HistoryItem[]>([]);
    const [page, setPage] = React.useState<Page>({ skip: 0, take: TAKE });
    const [total, setTotal] = React.useState(0);
    const [showPositions, setShowPositionHistory] = React.useState(true);
    const [showEvents, setShowEventHistory] = React.useState(true);
    const previousFlight = useRef<number>(undefined);
    const grid = useRef<GridHandle | null>(null);
    const debug = useDebugFeatures();
    const worker = useFlightWorker();
    const map = useMap();
    const mapPadding = useContext(MapPaddingContext);

    const fetch = (skip: number) => {
        queryFlightHistory(worker, {
            state: { skip, take: TAKE },
            flightId: props.id,
            includePositions: showPositions,
            includeEvents: showEvents
        }).then((response) => {
            setTotal(response.total);
            const newItems = response.total !== items.length ? new Array(response.total) : [...items];
            response.data.forEach((x, i) => (newItems[i + skip] = x));
            setItems(newItems);
        });
    };

    const handlePageChanged = (e: GridPageChangeEvent) => {
        setPage(e.page);
        fetch(e.page.skip);
    };

    useEffect(() => {
        const flightChanged = previousFlight.current !== flight?.details.id;
        if (flight) {
            if (flightChanged) {
                setPage({ take: TAKE, skip: 0 });
                grid.current?.scrollIntoView({ rowIndex: 0 });
                fetch(0);
            } else {
                fetch(grid.current?.props.skip ?? 0);
            }
        } else {
            setTotal(0);
            setItems([]);
            setPage({ skip: 0, take: TAKE });
        }

        previousFlight.current = flight?.details.id;
    }, [flight, showPositions, showEvents]);

    const getEventInfo = useCallback((code: number) => eventDefinitions[code] ?? null, [eventDefinitions]);
    const DetailsCell = useMemo(() => getDetailsCell(getEventInfo), [getEventInfo]);

    return (
        <div className="h-100 min-h-0 d-flex flex-column">
            <div className="px-2 py-1 d-flex gap-2">
                <Checkbox value={showPositions} label="Position history" onChange={(e) => setShowPositionHistory(e.value)} />
                <Checkbox value={showEvents} label="Events" onChange={(e) => setShowEventHistory(e.value)} />
            </div>
            <Grid
                ref={grid}
                className="telerik-cobaltify"
                dataItemKey="id"
                rowHeight={25}
                pageSize={page.take}
                total={total}
                skip={page.skip}
                resizable
                style={{ height: '100%', minHeight: 0, width: total === 0 ? 800 : undefined }}
                onPageChange={handlePageChanged}
                data={items.slice(page.skip, page.skip + page.take)}
                fixedScroll={true}
                onRowClick={(e) => {
                    const item = e.dataItem as HistoryItem;
                    if (item.type === FlightHistoryItemEnum.event) {
                        return;
                    }
                    map?.goToFlight(Coordinate.fromArray(item.position), map.getZoom(), mapPadding);
                }}
                // This is completely bugged. If you add items to the list and you are not at the top of the grid, onPageChange will not fire when you scroll to the top and the grid will just go black. To fix this you need to scroll down a bit the scroll up down up and eventually it'll reset to skip 0... absolute garbage...
                // Ohh, and if you grab the scroll bar and scroll faster than usual it'll just go black again...
                scrollable="virtual">
                <GridNoRecords>
                    <div>No position history</div>
                </GridNoRecords>
                <GridColumn id="te" field="te" title="Time" width={100} cells={{ data: CustomCell(TimeCell) }} />
                <GridColumn id="tp" field="tp" title="Type" width={60} cells={{ data: CustomCell(TypeCell) }} />
                <GridColumn
                    id="fd"
                    field="fd"
                    title="Source"
                    width={125}
                    cells={{ data: CustomCell(debug ? FeedIdDebugCell : FeedIdCell) }}
                />
                <GridColumn
                    id="lt"
                    field="lt"
                    title="Latitude"
                    width={90}
                    cells={{
                        data: CustomCell(
                            (x) => (x.type === FlightHistoryItemEnum.position && UnitFormatter.formatLatValue(x.position[1])) || '-'
                        )
                    }}
                />
                <GridColumn
                    id="ln"
                    field="ln"
                    title="Longitude"
                    width={90}
                    cells={{
                        data: CustomCell(
                            (x) => (x.type === FlightHistoryItemEnum.position && UnitFormatter.formatLngValue(x.position[0])) || '-'
                        )
                    }}
                />
                <GridColumn id="at" field="at" title="Altitude" width={70} cells={{ data: CustomCell(AltitudeCell) }} />
                <GridColumn id="sd" field="sd" title="Speed" width={60} cells={{ data: CustomCell(SpeedCell) }} />
                <GridColumn id="fob" field="fob" title="FOB" width={70} cells={{ data: CustomCell(FobCell) }} />
                <GridColumn id="details" field="details" title="Details" width={70} cells={{ data: CustomCell(DetailsCell) }} />
            </Grid>
        </div>
    );
}

function TimeCell(item: HistoryItem) {
    return DateTimeFormatter.formatTime(item.time, 'hms');
}

const typeNames: Record<FlightHistoryItemEnum, string> = {
    [FlightHistoryItemEnum.event]: 'Event',
    [FlightHistoryItemEnum.position]: 'Position'
};

function TypeCell(item: HistoryItem) {
    return <>{typeNames[item.type]}</>;
}

function FeedIdCell(item: HistoryItem) {
    const feedId = item.feedId;
    return (
        <>
            {feedId == null && '-'}
            {feedId != null && commonFormatter.formatFeedId(feedId)}
        </>
    );
}

function FeedIdDebugCell(item: HistoryItem) {
    const feedId = item.feedId;

    return (
        <>
            {feedId == null && '-'}
            {feedId != null && commonFormatter.formatFeedId(feedId)}
            <span> [{feedId ?? '?'}]</span>
            {/* <span>{item.id}</span> */}
        </>
    );
}

function AltitudeCell(item: HistoryItem) {
    let text = '-';
    if (item.type === FlightHistoryItemEnum.position) {
        text = item.onGround ? 'GND' : item.altitude !== null ? UnitFormatter.formatAltitude(item.altitude) : '-';
    }

    return text;
}

function SpeedCell(item: HistoryItem) {
    let text = '-';
    if (item.type === FlightHistoryItemEnum.position) {
        text = UnitFormatter.formatSpeed(item.speed) || '-';
    }

    return text;
}

function FobCell(item: HistoryItem) {
    if (item.type === FlightHistoryItemEnum.event) {
        return <>-</>;
    }

    return (
        <>
            {item.fob == null && '-'}
            {item.fob != null && UnitFormatter.formatFuel(Fuel.fromRouteFlown(item.fob, item.fobUnit))}
        </>
    );
}

function getDetailsCell(getEventDefinition: (code: number) => EventDefinition | null) {
    return function (item: HistoryItem) {
        let text = '';

        if (item.type === FlightHistoryItemEnum.event) {
            const definition = getEventDefinition(item.eventCode);
            if (item.eventCode && definition) {
                text += definition.displayName;
            }
            if (item.details) {
                if (text) {
                    text += ' ';
                }
                text += item.details;
            }
        }

        return text || '-';
    };
}

function CustomCell(formatFunc: (e: HistoryItem) => React.ReactNode) {
    return function (props: GridCustomCellProps) {
        return (
            <td {...props.tdProps} style={{ padding: '0.3rem 0.2rem' }}>
                {props.dataItem && props.field && formatFunc(props.dataItem)}
            </td>
        );
    };
}
